import cx from "classnames";
import { DmmHeader } from "components/DMMHeader";
import { Modal } from "components/Modal";
import { SNSButtons } from "components/SNSButtons";
import { DownloadButton } from "components/DownloadButton";
import { StaticImage } from "gatsby-plugin-image";
import { Link, useI18next } from "gatsby-plugin-react-i18next";
import { ReactComponent as MenuIcon } from "icons/burger.svg";
import { ReactComponent as TwitterIcon } from "icons/twitter.svg";
import { ReactComponent as MobileSocialBorderIcon } from "icons/mobile-social-border.svg";
import { Links } from "libs/constants";
import React, { useState } from "react";

import { useMediaQuery } from "@react-hookz/web";

type TLink = { display: string; link: Links };

export const Header = () => {
	const { t } = useI18next();
	const navItems: TLink[] = [
		{ display: t("nav.home"), link: Links.Home },
		{ display: t("nav.announcements"), link: Links.Announcements },
		{
			display: t("nav.world_overview"),
			link: Links.WorldOverview,
		},
		{ display: t("nav.characters"), link: Links.Characters },
		{ display: t("nav.feature"), link: Links.Feature },
		{ display: t("nav.contact"), link: Links.Contact },
	];
	const shouldChangeLayout = useMediaQuery("(min-width: 1298px)");
	const [modalOpen, setModalOpen] = useState(false);
	const handleModalOpen = () => setModalOpen(true);
	const handleModalClose = () => setModalOpen(false);

	return (
		<>
			<header className="fixed top-0 inset-x-0 z-20">
				<DmmHeader />
				<div className={cx("relative flex items-center justify-end p-4",
					{
						"lg:px-5 lg:bg-black/50 lg:justify-center lg:py-2":
							shouldChangeLayout,
					})}>
					<DownloadButton className="px-3 py-1 mr-3 text-white rounded-md lg:hidden bg-primary">
						{t('button.download')}
					</DownloadButton>
					<button
						type="button"
						className={cx("mr-2", { "lg:hidden": shouldChangeLayout })}
						onClick={handleModalOpen}
					>
						<MenuIcon width={32} height={25} />
					</button>
					<div className={cx("hidden", { "lg:block": shouldChangeLayout })}>
						<Nav items={navItems} />
					</div>
					<div className={cx("hidden absolute -right-[20px] top-0", { "lg:block": shouldChangeLayout })}>
						<div className="grid w-[280px]">
							<StaticImage
								src="../images/nav-bg.png"
								alt=""
								layout="fullWidth"
								className="col-span-full row-span-full"
							/>
							<nav className="row-span-full col-span-full pl-5 pt-2 flex justify-start space-x-2">
								<div className="h-[52px] flex items-center z-10 space-x-[1px]">
									<p className={cx([
										"w-[43px] h-[26px] pt-0.5 bg-[#011C61] custom-left-triangle-clip",
										"text-sm font-semibold text-right leading-[22px] tracking-[4px] pointer-events-none"
									])}>公式</p>
									<SNSButtons />
								</div>
								<DownloadButton className="group outline-none relative w-[130px] h-[52px]">
									<div className="absolute top-0 left-0 group-hover:hidden">
										<StaticImage
											src="../images/button-download.png"
											alt="Download"
										/>
									</div>
									<div className="absolute top-0 left-0 hidden group-hover:block">
										<StaticImage
											src="../images/button-active-download.png"
											alt="Download"
										/>
									</div>
									<p className="absolute inset-3 text-xl font-bold">{t('button.download')}</p>
								</DownloadButton>
							</nav>
						</div>
					</div>
				</div>
			</header>
			<Modal
				showClose
				open={modalOpen && !shouldChangeLayout}
				onClose={handleModalClose}
			>
				<div className="px-9">
					<NavMobile items={navItems} onClick={handleModalClose} />
					<hr className="mx-auto w-full max-w-[300px] border-primary my-10" />
					<nav className="pb-9">
						<ul className="flex items-center justify-center space-x-12">
							<li>
								<SNSItem href={Links.Twitter}>
									<div className="relative flex justify-center items-center">
										<MobileSocialBorderIcon className="absolute w-[39px] h-[39px]" />
										<TwitterIcon className="absolute w-[26px] h-[18px]" />
									</div>
								</SNSItem>
							</li>
						</ul>
					</nav>
				</div>
			</Modal>
		</>
	);
};

const SNSItem = ({
	href,
	children,
}: {
	href: string;
	children: React.ReactNode;
}) => (
	<a href={href} target="_blank" rel="noreferrer" className="text-primary">
		{children}
	</a>
);

const LinkItem = (props: TLink & { className?: string, onClick?: () => void }) => {
	const { link, display, className = "", onClick = () => {} } = props;

	if (link.includes('https://')) {
		return (
			<a
				href={link}
				target="_blank"
				rel="noopener noreferrer"
				className={className}
				onClick={onClick}>
				{display}
			</a>
		);
	}

	return (
		<Link to={link} className={className} onClick={onClick}>
			{display}
		</Link>
	);
}

const Nav = (props: { items: TLink[] }) => {
	return (
		<nav>
			<ul className="flex flex-row divide-x divide-muted text-disabled border-x border-muted">
				{props.items.map(({ display, link }) => (
					<li key={display} className="px-6 leading-none">
						<LinkItem
							link={link}
							display={display}
							className="text-lg leading-none hover:text-primary"
						/>
					</li>
				))}
			</ul>
		</nav>
	);
};

const NavMobile = (props: { items: TLink[], onClick: () => void }) => {
	return (
		<nav>
			<ul className="flex flex-col items-center space-y-8">
				{props.items.map(({ display, link }) => (
					<li key={display}>
						<LinkItem
							link={link}
							display={display}
							className="text-xl text-primary"
							onClick={props.onClick}
						/>
					</li>
				))}
			</ul>
		</nav>
	);
};
